import React, { useState, useEffect } from 'react';
import { Check, ArrowRight, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
// Razorpay loaded via script tag in index.html
import axios from 'axios';
import './Subscription.css';
import { subscriptionPlan } from '../mockData';
import { validatePincode } from '../utils/pincodeValidator';
import { useAuth } from '../context/AuthContext';
import SuccessModal from './SuccessModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Subscription = () => {
  // Razorpay available via window.Razorpay
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showProductSelection, setShowProductSelection] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [pincodeError, setPincodeError] = useState('');
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    street: '',
    city: '',
    state: '',
    pincode: ''
  });

  // Fetch products when component mounts
  useEffect(() => {
    const fetchProducts = async () => {
      setLoadingProducts(true);
      try {
        const response = await axios.get(`${API}/products/?available_only=true`);
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoadingProducts(false);
      }
    };
    
    fetchProducts();
  }, []);

  // Pre-fill form with user data when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || ''
      }));
      
      // Auto-select default address if available
      const defaultAddr = user.addresses?.find(addr => addr.isDefault);
      if (defaultAddr) {
        setSelectedAddress(defaultAddr);
        setFormData(prev => ({
          ...prev,
          street: defaultAddr.street,
          city: defaultAddr.city,
          state: defaultAddr.state,
          pincode: defaultAddr.pincode
        }));
      }
    }
  }, [isAuthenticated, user]);

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Validate pincode when user types
    if (name === 'pincode' && value.length === 6) {
      const result = await validatePincode(value);
      if (!result.valid) {
        setPincodeError('Sorry, we don\'t deliver to this pincode yet.');
      } else {
        setPincodeError('');
      }
    } else if (name === 'pincode') {
      setPincodeError('');
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    
    // Validate product selection
    if (!selectedProduct) {
      alert('Please select a microgreen variety');
      return;
    }
    
    // Validate pincode before proceeding
    const pincodeValidation = await validatePincode(formData.pincode);
    if (!pincodeValidation.valid) {
      setPincodeError('Sorry, we don\'t deliver to this pincode yet. ');
      return;
    }
    
    setLoading(true);

    try {
      // Create order on backend with productId
      const { data } = await axios.post(`${API}/subscriptions/create-order`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        productId: selectedProduct.id,
        address: {
          street: formData.street,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode
        }
      });

      // Razorpay options
      const options = {
        key: data.keyId,
        amount: data.amount,
        currency: 'INR',
        order_id: data.razorpayOrderId,
        name: 'House of Microgreens',
        description: 'Monthly Subscription',
        handler: async (response) => {
          try {
            console.log('Subscription payment successful, verifying...', response);
            // Verify payment on backend
            const verifyData = await axios.post(`${API}/subscriptions/verify-payment`, {
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
              customerDetails: formData
            }, {
              withCredentials: true
            });

            console.log('Subscription verification successful:', verifyData);
            setShowSuccessModal(true);
            setShowForm(false);
            setFormData({ name: '', email: '', phone: '', street: '', city: '', state: '', pincode: '' });
          } catch (error) {
            console.error('Subscription verification error:', error);
            console.error('Error response:', error.response);
            console.error('Error data:', error.response?.data);
            
            let errorMsg = 'Payment verification failed. ';
            if (error.response?.data?.detail) {
              if (typeof error.response.data.detail === 'string') {
                errorMsg += error.response.data.detail;
              } else {
                errorMsg += JSON.stringify(error.response.data.detail);
              }
            } else if (error.message) {
              errorMsg += error.message;
            } else {
              errorMsg += 'Unknown error occurred';
            }
            errorMsg += '. Please contact support.';
            
            alert(errorMsg);
          } finally {
            setLoading(false);
          }
        },
        prefill: {
          name: formData.name,
          email: formData.email,
          contact: formData.phone
        },
        theme: {
          color: '#8FEC78'
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
          }
        }
      };

      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.open();
    } catch (error) {
      setLoading(false);
      alert('Failed to create order. Please try again.');
      console.error(error);
    }
  };

  return (
    <section id="subscription" className="subscription-section">
      <div className="container">
        <div className="section-header">
          <h2 className="heading-2">Choose Your Plan</h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)', marginTop: '1rem' }}>
            Fresh microgreens delivered to your doorstep every week
          </p>
        </div>
        <div className="subscription-container">
          <div className="subscription-card">
            <div className="subscription-header">
              <h3 className="heading-3">{subscriptionPlan.title}</h3>
              <div className="price-container">
                <span className="price-mrp">₹{subscriptionPlan.mrp}</span>
                <span className="price-offer">₹{subscriptionPlan.offerPrice}/month</span>
              </div>
              <p className="body-small savings-text">Save ₹{subscriptionPlan.mrp - subscriptionPlan.offerPrice} every month</p>
            </div>
            <div className="subscription-features">
              {subscriptionPlan.features.map((feature, index) => (
                <div key={index} className="feature-item">
                  <Check size={20} className="feature-icon" />
                  <span className="body-medium">{feature}</span>
                </div>
              ))}
            </div>
            
            {!showProductSelection && !showForm ? (
              <button className="btn-primary subscription-btn" onClick={() => {
                setShowProductSelection(true);
              }}>
                Start Subscription
                <ArrowRight size={20} />
              </button>
            ) : showProductSelection && !showForm ? (
              <div className="product-selection-container">
                <div className="selection-header">
                  <h4 className="selection-title">Choose Your Weekly Microgreen</h4>
                  <p className="selection-subtitle">
                    Select your favorite variety for weekly delivery
                  </p>
                </div>
                
                {loadingProducts ? (
                  <div className="loading-state">Loading varieties...</div>
                ) : (
                  <div className="product-options-list">
                    {products.map((product) => (
                      <div 
                        key={product.id} 
                        className={`product-selection-card ${selectedProduct?.id === product.id ? 'selected' : ''}`}
                        onClick={() => setSelectedProduct(product)}
                      >
                        <div className="radio-indicator">
                          <div className={`radio-circle ${selectedProduct?.id === product.id ? 'checked' : ''}`}>
                            {selectedProduct?.id === product.id && <Check size={16} strokeWidth={3} />}
                          </div>
                        </div>
                        
                        <div className="product-image-wrapper">
                          <img src={product.image} alt={product.name} className="product-img" />
                        </div>
                        
                        <div className="product-details">
                          <h5 className="product-name">{product.name}</h5>
                          <p className="product-quantity">{product.quantity}</p>
                          {product.tasteProfile && (
                            <p className="product-taste">{product.tasteProfile}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="selection-actions">
                  <button 
                    className="btn-primary btn-continue" 
                    onClick={() => {
                      if (!selectedProduct) {
                        alert('Please select a microgreen variety');
                        return;
                      }
                      setShowForm(true);
                      setShowProductSelection(false);
                      // Scroll subscription section into view after form renders
                      setTimeout(() => {
                        document.getElementById('subscription')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 100);
                    }}
                    disabled={!selectedProduct || loadingProducts}
                  >
                    Continue to Address
                    <ArrowRight size={20} />
                  </button>
                  <button 
                    type="button" 
                    className="btn-secondary btn-cancel" 
                    onClick={() => {
                      setShowProductSelection(false);
                      setSelectedProduct(null);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="subscription-form">
                {!isAuthenticated && (
                  <div className="signin-note" data-testid="signin-note">
                    <AlertCircle size={16} />
                    <span><button type="button" className="signin-link" onClick={() => navigate('/login', { state: { from: { pathname: '/#subscription' } } })}>Sign in</button> to track your orders and manage cancellations</span>
                  </div>
                )}
                {selectedProduct && (
                  <div className="selected-product-display">
                    <img src={selectedProduct.image} alt={selectedProduct.name} className="selected-product-thumb" />
                    <div>
                      <p className="body-small" style={{ color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                        Selected Variety
                      </p>
                      <p className="body-medium" style={{ fontWeight: '600' }}>
                        {selectedProduct.name}
                      </p>
                    </div>
                  </div>
                )}
                {isAuthenticated && user?.addresses?.length > 0 && (
                  <div className="saved-addresses">
                    <label className="form-label">Select Delivery Address:</label>
                    <div className="address-options">
                      {user.addresses.map((addr) => (
                        <div 
                          key={addr.id} 
                          className={`address-option ${selectedAddress?.id === addr.id ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedAddress(addr);
                            setFormData(prev => ({
                              ...prev,
                              street: addr.street,
                              city: addr.city,
                              state: addr.state,
                              pincode: addr.pincode
                            }));
                          }}
                        >
                          <div className="address-text">
                            {addr.street}, {addr.city}, {addr.state} - {addr.pincode}
                          </div>
                          {addr.isDefault && <span className="default-badge">Default</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="form-row">
                  <input
                    type="text"
                    name="name"
                    placeholder="Full Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input"
                    disabled={isAuthenticated}
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                    disabled={isAuthenticated}
                  />
                </div>
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
                <input
                  type="text"
                  name="street"
                  placeholder="Street Address"
                  value={formData.street}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
                <div className="form-row">
                  <input
                    type="text"
                    name="city"
                    placeholder="City"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    className="form-input"
                  />
                  <input
                    type="text"
                    name="state"
                    placeholder="State"
                    value={formData.state}
                    onChange={handleChange}
                    required
                    className="form-input"
                  />
                </div>
                <input
                  type="text"
                  name="pincode"
                  placeholder="Pincode"
                  value={formData.pincode}
                  onChange={handleChange}
                  required
                  className={`form-input ${pincodeError ? 'error' : ''}`}
                  maxLength="6"
                />
                {pincodeError && (
                  <div className="error-message">
                    <AlertCircle size={16} />
                    <span>{pincodeError}</span>
                  </div>
                )}
                <div className="form-actions">
                  <button type="submit" className="btn-primary" disabled={loading}>
                    {loading ? 'Processing...' : 'Proceed to Payment'}
                    <ArrowRight size={20} />
                  </button>
                  <button type="button" className="btn-secondary" onClick={() => {
                    setShowForm(false);
                    setShowProductSelection(true);
                  }}>
                    Back to Product Selection
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      <SuccessModal 
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
        }}
      />
    </section>
  );
};

export default Subscription;
